import { FleeterIcon } from "@features/copilot/components";
import { dayjs } from "@lib";
import { User01 } from "@untitledui/icons";
import { twMerge } from "tailwind-merge";
import {
	DEFAULT_INFRACTION_HISTORY_EVENT_STYLE,
	INFRACTION_HISTORY_ACTOR_TYPE,
	INFRACTION_HISTORY_EVENT_STYLES,
	INFRACTION_HISTORY_EVENT_TYPE,
} from "../constants/infraction-history-event.constant";
import type { InfractionHistoryItem } from "../types/infraction.types";

type InfractionHistoryTimelineProps = {
	history: InfractionHistoryItem[];
};

function formatHistoryDate(item: InfractionHistoryItem): string {
	if (item.occurredAt) {
		return dayjs(item.occurredAt).format("DD/MM/YYYY - HH:mm");
	}

	if (item.referenceDate) {
		return dayjs(item.referenceDate).format("DD/MM/YYYY");
	}

	return "—";
}

function getHistoryActorLabel(item: InfractionHistoryItem): string {
	if (item.actorType === INFRACTION_HISTORY_ACTOR_TYPE.SYSTEM) {
		return item.actorName ?? "Sistema Fleeter";
	}

	return item.actorName ?? "—";
}

function HistoryEventIcon({ type }: { type: number }) {
	const style =
		INFRACTION_HISTORY_EVENT_STYLES[type] ??
		DEFAULT_INFRACTION_HISTORY_EVENT_STYLE;
	const Icon = style.Icon;

	return (
		<div
			className={twMerge(
				"relative z-10 flex size-8 shrink-0 items-center justify-center rounded-full",
				style.root
			)}
		>
			<Icon className={twMerge("size-4", style.icon)} />
		</div>
	);
}

function HistoryEventDescription({ item }: { item: InfractionHistoryItem }) {
	switch (item.type) {
		case INFRACTION_HISTORY_EVENT_TYPE.AUTOMATED_CREATED:
			return (
				<p className="mt-0.5 text-[#424754] text-xs leading-[18px]">
					Infração criada automaticamente pelo sistema.
				</p>
			);
		case INFRACTION_HISTORY_EVENT_TYPE.MANUAL_CREATED:
			return (
				<p className="mt-0.5 text-[#424754] text-xs leading-[18px]">
					Infração criada manualmente no sistema.
				</p>
			);
		case INFRACTION_HISTORY_EVENT_TYPE.DRIVER_ASSIGNED:
			return (
				<p className="mt-0.5 text-fg-secondary text-xs leading-[18px]">
					Infração atribuída manualmente para{" "}
					{item.driverName ? (
						<span className="inline-flex items-center rounded-sm bg-[rgba(21,112,239,0.1)] px-0.5 font-medium text-fg-brand-secondary text-sm leading-5">
							{item.driverName}
						</span>
					) : (
						"o condutor"
					)}
					.
				</p>
			);
		case INFRACTION_HISTORY_EVENT_TYPE.DRIVER_INDICATION_EXPIRED:
			return (
				<p className="mt-0.5 text-fg-secondary text-xs leading-[18px]">
					Prazo expirado para indicação.
				</p>
			);
		case INFRACTION_HISTORY_EVENT_TYPE.PAID:
			return (
				<p className="mt-0.5 text-fg-secondary text-xs leading-[18px]">
					Status da infração alterado para{" "}
					<span className="inline-flex items-center rounded-sm bg-bg-success-primary px-0.5 font-medium text-sm text-success-primary leading-5">
						{item.typeLabel ?? "Infração Paga"}
					</span>
					.
				</p>
			);
		case INFRACTION_HISTORY_EVENT_TYPE.PAYMENT_EXPIRED:
			return (
				<p className="mt-0.5 text-fg-secondary text-xs leading-[18px]">
					Status da infração alterado para{" "}
					<span className="inline-flex items-center rounded-sm bg-bg-error-primary px-0.5 font-medium text-error-primary text-sm leading-5">
						{item.typeLabel ?? "Pagamento Vencido"}
					</span>
					.
				</p>
			);
		default:
			return null;
	}
}

function HistoryEventActor({ item }: { item: InfractionHistoryItem }) {
	const actorLabel = getHistoryActorLabel(item);
	const isSystemActor = item.actorType === INFRACTION_HISTORY_ACTOR_TYPE.SYSTEM;

	if (actorLabel === "—") {
		return null;
	}

	return (
		<div className="mt-1 flex items-center gap-1.5">
			{isSystemActor ? (
				<span className="flex size-3.5 shrink-0 items-center justify-center rounded-full bg-bg-primary">
					<FleeterIcon className="text-brand-600" size={10} />
				</span>
			) : (
				<User01 aria-hidden className="size-3 shrink-0 text-fg-quaternary" />
			)}
			<span className="text-[#424754] text-xs leading-[18px]">
				{actorLabel}
			</span>
		</div>
	);
}

export function InfractionHistoryTimeline({
	history,
}: InfractionHistoryTimelineProps) {
	if (history.length === 0) {
		return null;
	}

	return (
		<section
			className="flex flex-col gap-4"
			data-slot="infraction-history-timeline"
		>
			<h3 className="font-bold text-fg-primary text-sm uppercase tracking-wide">
				Histórico da Infração
			</h3>
			<div className="relative flex flex-col gap-4">
				<div
					aria-hidden
					className="absolute top-4 bottom-4 left-4 w-px bg-border-secondary"
				/>
				{history.map((item, index) => {
					const formattedDate = formatHistoryDate(item);

					return (
						<div
							className="relative flex gap-3"
							key={`${item.type}-${item.occurredAt ?? index}`}
						>
							<HistoryEventIcon type={item.type} />
							<div className="min-w-0 flex-1 pt-0.5">
								<div className="flex items-start justify-between gap-3">
									<p className="font-semibold text-fg-secondary text-sm leading-5">
										{item.typeLabel ?? "Evento"}
									</p>
									<p className="shrink-0 font-medium text-[#424754] text-xs leading-[18px]">
										{formattedDate}
									</p>
								</div>
								<HistoryEventDescription item={item} />
								<HistoryEventActor item={item} />
							</div>
						</div>
					);
				})}
			</div>
		</section>
	);
}
